/*
 * variant-ticker.js — gently auto-scrolls a big set of option swatches like a
 * ticker so shoppers notice there are more than fit on screen:
 *   • slow constant drift (ping-pongs at the ends)
 *   • moves FASTER while the page is being scrolled (scroll velocity)
 *   • the shopper can STOP it by tapping/clicking, and SWIPE it by dragging
 *     (native horizontal scroll) — any interaction hands control to the user.
 * Honours prefers-reduced-motion.
 *
 * Two targets:
 *   1. NATIVE Dawn picker — <fieldset data-variant-ticker> .vticker (see
 *      product-variant-picker.liquid). Dormant on this store because the native
 *      picker is hidden, kept for any future native-swatch product.
 *   2. KING Product Options image swatches — this store renders large image
 *      options (e.g. "Design", 18 tiles) as <ul.swatch-view-image>, an app-
 *      injected grid that otherwise stacks very tall on phones. On MOBILE we
 *      tag qualifying (>4-tile) groups with .vticker-king (premium.css lays them
 *      out as one horizontal strip) and drift them with the same engine. King
 *      injects/re-renders asynchronously, so we re-scan on a MutationObserver.
 */
(function () {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var MOBILE = window.matchMedia ? window.matchMedia('(max-width: 749px)') : { matches: false, addListener: function () {} };

  // Attach the auto-scroll drift to any horizontally-scrollable element. The loop
  // is lazy: it only scrolls once the element actually overflows, so it copes with
  // swatches that finish rendering (or a viewport that rotates into mobile) after
  // init. Self-terminates if the element leaves the DOM (King re-renders).
  function initTicker(view) {
    if (view.__vticker) return; // already wired
    view.__vticker = true;

    var maxScroll = function () { return view.scrollWidth - view.clientWidth; };

    var dir = 1;             // start scrolling content right→left (pos/scrollLeft grows)
    var MIN_SPEED = 0.2;     // px/frame near a turn (glides to a near-stop)
    var MAX_SPEED = 1.0;     // px/frame at cruise (smooth + clearly moving)
    var EASE_PX = 90;        // how far from each end the ease-in/out spans
    var boost = 0;           // extra speed injected by page-scroll velocity
    var userStopped = false;
    // Let external controls (the prev/next buttons) hand control to the user too.
    view.__vtickerStop = function () { userStopped = true; };
    var pos = view.scrollLeft; // float accumulator — scrollLeft rounds to an int,
                               // so a <1px/frame drift would never accumulate if
                               // we read it back each frame.

    function frame() {
      if (!view.isConnected) { view.__vticker = false; return; } // detached → stop
      if (!userStopped) {
        var m = maxScroll();
        if (m > 2) {
          // Ease the speed toward MIN near either end so it glides and SLOWS into the
          // turn instead of snapping — a smooth, continuous right→left drift.
          var edge = Math.min(pos, m - pos);
          var ramp = Math.max(0, Math.min(1, edge / EASE_PX));
          var speed = MIN_SPEED + (MAX_SPEED - MIN_SPEED) * ramp;
          pos += dir * (speed + boost);
          if (pos >= m) { pos = m; dir = -1; }
          else if (pos <= 0) { pos = 0; dir = 1; }
          view.scrollLeft = pos;
          boost *= 0.9;      // decay the scroll boost back to the base drift
        } else {
          pos = view.scrollLeft; // not overflowing yet — keep pos in sync
        }
      }
      requestAnimationFrame(frame);
    }

    // Any direct interaction (tap to stop / swipe to move) hands control over.
    ['pointerdown', 'touchstart', 'wheel'].forEach(function (ev) {
      view.addEventListener(ev, function () { userStopped = true; }, { passive: true });
    });

    // Page-scroll velocity → temporary speed boost (before the user takes over).
    var lastY = window.pageYOffset;
    var lastT = performance.now();
    window.addEventListener('scroll', function () {
      if (userStopped) return;
      var now = performance.now();
      var dy = Math.abs(window.pageYOffset - lastY);
      var dt = Math.max(1, now - lastT);
      boost = Math.min(6, (dy / dt) * 3); // px/ms → capped boost
      lastY = window.pageYOffset;
      lastT = now;
    }, { passive: true });

    requestAnimationFrame(frame);
  }

  // A King image-swatch <ul> worth tickering: a product-page image group with
  // more than 4 tiles (not a collection-card swatch).
  function isKingCandidate(ul) {
    if (ul.querySelectorAll(':scope > .swatch-view-item').length <= 4) return false;
    if (!ul.closest('.product')) return false;            // PDP only
    if (ul.closest('.card, #product-grid')) return false; // not a collection-card swatch
    // NB: don't exclude .grid__item — Dawn wraps the PDP product-info column in it.
    return true;
  }

  // A small centered progress pill under the strip so shoppers see it's slidable.
  // The thumb width reflects how much fits; it slides as the strip auto-scrolls or
  // is swiped. One bar per container (rebuilt if King replaces the <ul>).
  function attachScrollIndicator(view) {
    if (view.__indicator && view.__indicator.isConnected) return;
    var parent = view.parentElement;
    if (!parent) return;
    var old = parent.querySelector(':scope > .vticker-king__bar');
    if (old) old.remove();

    var bar = document.createElement('div');
    bar.className = 'vticker-king__bar';
    bar.setAttribute('aria-hidden', 'true');
    var thumb = document.createElement('div');
    thumb.className = 'vticker-king__thumb';
    bar.appendChild(thumb);
    view.insertAdjacentElement('afterend', bar);
    view.__indicator = thumb;

    function update() {
      var max = view.scrollWidth - view.clientWidth;
      if (max <= 2) { bar.style.display = 'none'; return; }
      bar.style.display = '';
      var wpct = Math.max(16, (view.clientWidth / view.scrollWidth) * 100);
      var lpct = (view.scrollLeft / max) * (100 - wpct);
      thumb.style.width = wpct + '%';
      thumb.style.marginLeft = lpct + '%';
    }
    // Programmatic scrollLeft (auto-drift) and user swipes both fire 'scroll'.
    view.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    setTimeout(update, 0);
    view.__indicatorUpdate = update;
  }

  // Clickable prev/next buttons so the strip is obviously scrollable (an explicit
  // control in addition to the auto-drift + swipe). Shown on desktop AND mobile.
  // Positioned over the vertical centre of the strip (computed so it's correct
  // whatever the tile height / whether a label sits above the <ul>).
  function attachControls(view) {
    if (view.__controls && view.__controls.isConnected) return;
    var parent = view.parentElement;
    if (!parent) return;
    var stale = parent.querySelectorAll(':scope > .vticker-king__nav');
    for (var i = 0; i < stale.length; i++) stale[i].remove();
    if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';

    function make(dir, label, pts) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'vticker-king__nav vticker-king__nav--' + dir;
      b.setAttribute('aria-label', label);
      b.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="' + pts + '"></polyline></svg>';
      b.addEventListener('click', function (e) {
        e.preventDefault();
        if (view.__vtickerStop) view.__vtickerStop(); // clicking hands control to the user
        var tile = view.querySelector('.swatch-view-item');
        var step = (tile ? tile.getBoundingClientRect().width + 10 : 120) * 2;
        view.scrollBy({ left: dir === 'next' ? step : -step, behavior: 'smooth' });
      });
      return b;
    }
    var prev = make('prev', 'Previous options', '15 18 9 12 15 6');
    var next = make('next', 'Next options', '9 18 15 12 9 6');
    parent.appendChild(prev);
    parent.appendChild(next);
    view.__controls = prev;

    function place() {
      var top = view.offsetTop + view.clientHeight / 2;
      prev.style.top = top + 'px';
      next.style.top = top + 'px';
      // Hide the buttons entirely when the strip doesn't overflow (nothing to scroll).
      var overflow = view.scrollWidth - view.clientWidth > 4;
      prev.style.display = overflow ? '' : 'none';
      next.style.display = overflow ? '' : 'none';
    }
    place();
    window.addEventListener('resize', place);
    view.addEventListener('scroll', place, { passive: true });
    setTimeout(place, 300);
    setTimeout(place, 1200);
    view.__controlsPlace = place;
  }

  function wireKing(ul) {
    if (!isKingCandidate(ul)) return;
    ul.classList.add('vticker-king');
    attachScrollIndicator(ul);
    attachControls(ul);
    initTicker(ul);
  }

  function scan() {
    // 1) native Dawn ticker (any viewport)
    document.querySelectorAll('[data-variant-ticker] .vticker').forEach(initTicker);

    // 2) King image swatches — desktop AND mobile (single-line auto-scroll strip)
    document.querySelectorAll('ul.swatch-view-image').forEach(wireKing);
  }

  function boot() {
    scan();

    // King injects / re-renders swatches asynchronously — re-scan (debounced) as
    // the DOM changes so late or replaced groups still get wired.
    if (window.MutationObserver) {
      var t = null;
      new MutationObserver(function () {
        if (t) return;
        t = setTimeout(function () { t = null; scan(); }, 200);
      }).observe(document.body, { childList: true, subtree: true });
    }

    // Fallback re-scans + re-scan when the mobile breakpoint is crossed.
    window.addEventListener('load', scan);
    setTimeout(scan, 1500);
    setTimeout(scan, 4000);
    if (MOBILE.addEventListener) MOBILE.addEventListener('change', scan);
    else if (MOBILE.addListener) MOBILE.addListener(scan);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
